// src/utils/passwordValidation.js
// Password validation utility based on business security settings

import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

/**
 * Default password policy (used when no business settings exist)
 */
export const DEFAULT_POLICY = {
  minPasswordLength: 8,
  requireUppercase: true,
  requireNumbers: true,
  requireSpecialChars: false,
};

/**
 * Fetch password policy for a business
 */
export async function getPasswordPolicy(businessId) {
  if (!businessId) return DEFAULT_POLICY;

  try {
    const docRef = doc(db, "businesses", businessId, "settings", "security");
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        minPasswordLength: data.minPasswordLength || DEFAULT_POLICY.minPasswordLength,
        requireUppercase: data.requireUppercase ?? DEFAULT_POLICY.requireUppercase,
        requireNumbers: data.requireNumbers ?? DEFAULT_POLICY.requireNumbers,
        requireSpecialChars: data.requireSpecialChars ?? DEFAULT_POLICY.requireSpecialChars,
        passwordExpiryDays: data.passwordExpiryDays || 0,
      };
    }
  } catch (error) {
    console.error("Error fetching password policy:", error);
  }

  return DEFAULT_POLICY;
}

/**
 * Validate password against policy
 * @param {string} password - The password to validate
 * @param {object} policy - The password policy to validate against
 * @returns {object} - { isValid: boolean, errors: string[] }
 */
export function validatePassword(password, policy = DEFAULT_POLICY) {
  const errors = [];

  if (!password) {
    return { isValid: false, errors: ["Password is required"] };
  }

  // Check minimum length
  if (password.length < policy.minPasswordLength) {
    errors.push(`Password must be at least ${policy.minPasswordLength} characters`);
  }

  // Check uppercase requirement
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one uppercase letter");
  }

  // Check number requirement
  if (policy.requireNumbers && !/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  // Check special character requirement
  if (policy.requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push("Password must contain at least one special character (!@#$%^&*)");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Get password strength score (0-100)
 */
export function getPasswordStrength(password) {
  if (!password) return 0;

  let score = 0;

  // Length score (up to 30 points)
  score += Math.min(password.length * 3, 30);

  // Character variety (up to 40 points)
  if (/[a-z]/.test(password)) score += 10;
  if (/[A-Z]/.test(password)) score += 10;
  if (/[0-9]/.test(password)) score += 10;
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 10;

  // Bonus for mixed patterns (up to 30 points)
  if (password.length >= 12) score += 10;
  if (/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) score += 10;
  if (/(?=.*[!@#$%^&*])/.test(password) && password.length >= 10) score += 10;

  return Math.min(score, 100);
}

/**
 * Get strength label and color
 */
export function getStrengthLabel(score) {
  if (score < 30) return { label: "Weak", color: "text-red-500", bgColor: "bg-red-500" };
  if (score < 50) return { label: "Fair", color: "text-orange-500", bgColor: "bg-orange-500" };
  if (score < 70) return { label: "Good", color: "text-yellow-500", bgColor: "bg-yellow-500" };
  if (score < 90) return { label: "Strong", color: "text-emerald-500", bgColor: "bg-emerald-500" };
  return { label: "Very Strong", color: "text-emerald-600", bgColor: "bg-emerald-600" };
}

/**
 * Password strength indicator component helper data
 */
export function getPasswordRequirements(policy = DEFAULT_POLICY) {
  const requirements = [
    {
      key: "length",
      label: `At least ${policy.minPasswordLength} characters`,
      test: (pwd) => pwd.length >= policy.minPasswordLength,
    },
  ];

  if (policy.requireUppercase) {
    requirements.push({
      key: "uppercase",
      label: "One uppercase letter",
      test: (pwd) => /[A-Z]/.test(pwd),
    });
  }

  if (policy.requireNumbers) {
    requirements.push({
      key: "number",
      label: "One number",
      test: (pwd) => /[0-9]/.test(pwd),
    });
  }

  if (policy.requireSpecialChars) {
    requirements.push({
      key: "special",
      label: "One special character",
      test: (pwd) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
    });
  }

  return requirements;
}

export default {
  getPasswordPolicy,
  validatePassword,
  getPasswordStrength,
  getStrengthLabel,
  getPasswordRequirements,
  DEFAULT_POLICY,
};
